// © Microsoft Corporation. All rights reserved.

import { MsftStripe, MsftContentCard, MsftContentIndicator, telemetry } from "@fast/components";
import { FluentAnchor, FluentCard, FluentFlipper } from "@fluentui/web-components";
import { html, repeat, when } from "@microsoft/fast-element";
import { PublisherCarousel } from "./PublisherCarousel";
import { PublisherCarouselThemeStyles } from "./PublisherCarousel.styles";
import { PublisherCardData, PublisherCarouselTheme } from "./PublisherCarouselData";
import { renderHybridTelemetryAsync } from "./PublisherCarousel.telemetry";
import { generatePublisherString } from "./PublisherCarouselDataMapper";
import { getDateString } from "@msnews/datetime-util";
import { PublisherCarouselStrings } from "./PublisherCarousel.strings";

FluentAnchor;
FluentCard;
FluentFlipper;
MsftStripe;
MsftContentCard;
MsftContentIndicator;

const chevronGlyph = html<PublisherCarousel>`
    <span slot="details" class="chevron-glyph" title="${x => x.strings.aboutSlugText}">
        <svg width="7" height="12" viewBox="0 0 7 12" xmlns="http://www.w3.org/2000/svg"><path d="m0.64582 0.14708c0.19491-0.19561 0.51149-0.19617 0.7071-0.00126l5.4845 5.465c0.21571 0.21494 0.21571 0.56425 0 0.7792l-5.4845 5.465c-0.19561 0.1949-0.51219 0.1943-0.7071-0.0013s-0.19435-0.5122 0.00126-0.7071l5.1646-5.1462-5.1646-5.1462c-0.19561-0.19492-0.19617-0.5115-0.00126-0.70711z" fill-opacity=".83"/></svg>
    </span>
`;

const carouselTitleTemplate = html<PublisherCarousel>`
    ${x => x.strings?.morePublisherContentTitle &&  html`
        <a class="carousel-title" target="_blank" href="${x => x.carouselData?.seeAllLinkUrl}" ${telemetry(x => x.telemetryContext?.seeAllLink?.getMetadataTag())}>
            ${x => x.carouselData.title}
            ${when(x => !x.hideChevronIcon, chevronGlyph)}
        </a>
    `}
`;

const carouselSeeAllLinkTemplate = html<PublisherCarousel>`
    <fluent-anchor class="action" appearance="lightweight" slot="action" target="_blank" href="${x => x.carouselData.seeAllLinkUrl}"
        ${telemetry(x => x.telemetryContext?.seeAllLink?.getMetadataTag())}
        ht-id=${x => renderHybridTelemetryAsync(x.telemetryContext?.seeAllLink, true, {anchorParent: true, isNavigate: true})}
    >
        ${x => x.strings.viewProfileLinkText}
    </fluent-anchor>
`;

const imageTemplateForSize = (cardImageSize: { height: number; width: number }, slot = "media") => html<PublisherCardData>`
    ${when(x => x.imageInfo?.sourceImageUrl, html`
        <img
            slot="${slot}"
            height="${cardImageSize.height}"
            width="${cardImageSize.width}"
            src="${x => x.imageInfo?.sourceImageUrl}"
            alt="${x => x.imageInfo?.altText}"
        />
    `)}
    ${when(x => !x.imageInfo?.sourceImageUrl, html`
        <div
            slot="${slot}"
            style="height: ${cardImageSize.height}px; width: ${cardImageSize.width}px"
        >
        </div>
    `)}
`;

const getContentIndicator = (icon: string) => html`
    <msft-content-indicator slot="content-indicator" style="--corner-radius: 4">
        ${icon}
    </msft-content-indicator>
`;

const subscriptionBadgeTemplate = (localizedStrings: PublisherCarouselStrings) => html<PublisherCardData>`
    <div slot="attribution" class="subscription-badge-container">
        <label class="subscription-badge${x => x.publisher.isSubscribed ? " subscribed" : ""}">
        ${x => x.publisher.isSubscribed ? localizedStrings?.badgeLabelSubscribed : localizedStrings?.badgeLabelNotSubscribed}
        </label>
    </div>
`;

export const providerTemplate = (customizedDate: boolean) => html<PublisherCardData>`
    ${when(x => x.publisher?.logoUrl, html<PublisherCardData>`
        <img
            slot="image"
            src="${x => x.publisher?.logoUrl}"
        />
    `)}
    ${when(x => customizedDate, html`<span>${x => x.publisher?.name}</span>`)}
    ${when(x => !customizedDate, html`<span>${x => generatePublisherString(x.publisher?.name, x.publishedDateTime && getDateString(new Date(x.publishedDateTime), x.locale, x.strings, 1))}</span>`)}
`;

const cardTemplate = (theme: PublisherCarouselTheme) => html<PublisherCardData>`
    <fluent-card class="publisher-card">
        ${() => PublisherCarouselThemeStyles[theme]?.card && html`
            <style> ${PublisherCarouselThemeStyles[theme]?.card.styles.toString()} </style>
        `}
        <msft-content-card
            href="${x => x.clickUrl}"
            target="_blank"
            :anchorTelemetryTag=${x => x.telemetry?.destination?.getMetadataTag()}
            ht-id=${x => renderHybridTelemetryAsync(x.telemetry?.destination, true, {anchorParent: true, isNavigate: true})}
        >
            ${x => x.contentIndicator && getContentIndicator(x.contentIndicator)}

            ${when((x, c) => c.parent?.showProviderAttribution, html`
                <msft-attribution slot="attribution">
                    ${providerTemplate(false)}
                </msft-attribution>
            `)}

            ${() => imageTemplateForSize(PublisherCarouselThemeStyles[theme]?.imageSize)}
            ${when((x, c) => !(c.parent?.showProviderAttribution) && x.isPremium, (x, c) => subscriptionBadgeTemplate(c.parent?.strings))}
            ${x => x.title}
        </msft-content-card>
    </fluent-card>
`;

const defaultTemplate = () => html<PublisherCarousel>`
    ${when(x => !x.backgroundSheetDisabled, html`<div class="background-sheet"></div>`)}
    <msft-stripe
        speed="4000"
        ${telemetry(x => x.telemetryContext?.carousel?.getMetadataTag())}
        ht-id=${x => renderHybridTelemetryAsync(x.telemetryContext?.carousel, false)}
    >
        ${x => PublisherCarouselThemeStyles[x.theme]?.carousel && html`
            <style> ${PublisherCarouselThemeStyles[x.theme]?.carousel.styles.toString()} </style>
        `}
        <div slot="heading">
            ${when(x => !x.headerDisabled, carouselTitleTemplate)}
        </div>
        <div slot="previous-flipper">
            <fluent-flipper direction="previous" ${telemetry(x => x.telemetryContext?.previousSlideArrow?.getMetadataTag())} ht-id=${x => renderHybridTelemetryAsync(x.telemetryContext?.previousSlideArrow, true)}></fluent-flipper>
        </div>
        <div slot="next-flipper">
            <fluent-flipper
                ${telemetry(x => x.telemetryContext?.nextSlideArrow?.getMetadataTag())}
                ht-id=${x => renderHybridTelemetryAsync(x.telemetryContext?.nextSlideArrow, true)}
                @click=${x => renderHybridTelemetryAsync(x.telemetryContext?.nextSlideArrow, true)}
            ></fluent-flipper>
        </div>
        ${when(x => !x.headerDisabled && !x.hideSeeAllLink && x.carouselData?.seeAllLinkUrl, carouselSeeAllLinkTemplate)}
        ${repeat(x => x.carouselData?.cards, x => cardTemplate(x.theme))}
    </msft-stripe>
`;

/** PublisherCarousel webcomponent template structure  */
export const PublisherCarouselTemplate = html<PublisherCarousel>`
    ${when(x => x.carouselData?.cards?.length > 0, defaultTemplate())}
`;